import React, { useState, useEffect } from 'react';
import { Company, CompanyMember, UserInvitation, UserRole } from '../types/company';
import { CompanyService } from '../services/companyService';
import './CompanyManagement.css';

interface CompanyManagementProps {
  currentUser: any;
  currentUserRole: string;
  onCompanySelect: (companyId: string) => void;
  showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

interface MemberWithNickname extends CompanyMember {
  nickname?: string;
  email?: string;
  displayName?: string;
}

const CompanyManagement: React.FC<CompanyManagementProps> = ({ 
  currentUser,
  currentUserRole,
  onCompanySelect, 
  showToast 
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [members, setMembers] = useState<MemberWithNickname[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newCompany, setNewCompany] = useState({
    name: '',
    description: '',
    industry: '',
    website: '',
  });

  const [inviteUser, setInviteUser] = useState({
    email: '',
    role: 'user' as UserRole,
  });

  const [editCompany, setEditCompany] = useState({
    name: '',
    description: '',
    industry: '',
    website: '',
  });

  useEffect(() => {
    loadUserCompanies();
  }, [currentUser]);

  const loadUserCompanies = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      // MASTER accounts should see ALL companies, not just their member companies
      // Use localStorage to avoid timing issues with state
      const userRole = localStorage.getItem('userRole') || 'USER';
      console.log('🔍 DEBUG: CompanyManagement - using role from localStorage:', userRole);
      
      const companies = userRole === 'MASTER' 
        ? await CompanyService.getAllCompanies()
        : await CompanyService.getUserCompanies(currentUser.uid);
        
      setCompanies(companies);
      
      if (companies.length > 0) {
        setSelectedCompany(companies[0]);
        loadCompanyDetails(companies[0].id);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      showToast('Error loading companies', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyDetails = async (companyId: string) => {
    try {
      const [membersData, invitationsData] = await Promise.all([
        CompanyService.getCompanyMembers(companyId),
        CompanyService.getPendingInvitations(companyId),
      ]);
      
      // Fetch user profiles to get nicknames
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const membersWithNicknames: MemberWithNickname[] = [];
      
      for (const member of membersData) {
        try {
          const userDocRef = doc(db, 'userProfiles', member.userId);
          const userSnapshot = await getDoc(userDocRef);
          
          if (userSnapshot.exists()) {
            const userData = userSnapshot.data();
            membersWithNicknames.push({
              ...member,
              nickname: userData.nickname,
              email: userData.email,
              displayName: userData.displayName
            });
            console.log('✅ Loaded member:', { userId: member.userId, nickname: userData.nickname, email: userData.email });
          } else {
            console.log('⚠️ No profile found for:', member.userId);
            membersWithNicknames.push(member);
          }
        } catch (error) {
          console.error('Error loading profile for:', member.userId, error);
          membersWithNicknames.push(member);
        }
      }
      
      // Filter out owner/admin users if current user is regular user
      const filteredMembers = ['MASTER', 'ADMIN', 'owner', 'admin'].includes(currentUserRole)
        ? membersWithNicknames
        : membersWithNicknames.filter(m => !['owner', 'admin'].includes(m.role));
      
      setMembers(filteredMembers);
      setInvitations(invitationsData);
    } catch (error) {
      console.error('Error loading company details:', error);
      showToast('Error loading company details', 'error');
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    try {
      const company = await CompanyService.createCompany({
        name: newCompany.name,
        description: newCompany.description,
        industry: newCompany.industry,
        website: newCompany.website,
        settings: {
          allowUserRegistration: true,
          requireEmailVerification: true,
          defaultUserRole: 'user',
        },
        createdBy: currentUser.uid,
        isActive: true,
      });

      // Add creator as owner
      await CompanyService.addCompanyMember({
        companyId: company.id,
        userId: currentUser.uid,
        role: 'owner',
        status: 'active',
        invitedBy: currentUser.uid,
        invitedAt: new Date().toISOString(),
        joinedAt: new Date().toISOString(),
        permissions: CompanyService.getDefaultPermissions('owner'),
      });

      showToast('Company created successfully!', 'success');
      setShowCreateCompany(false);
      setNewCompany({ name: '', description: '', industry: '', website: '' });
      loadUserCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
      showToast('Error creating company', 'error');
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    try {
      const invitationId = await CompanyService.createUserInvitation({
        companyId: selectedCompany.id,
        email: inviteUser.email,
        role: inviteUser.role,
        invitedBy: currentUser.uid,
        invitedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        status: 'pending',
        token: Math.random().toString(36).substring(2, 15),
      });

      showToast(`Invitation sent to ${inviteUser.email}`, 'success');
      setShowInviteUser(false);
      setInviteUser({ email: '', role: 'user' });
      loadCompanyDetails(selectedCompany.id);
    } catch (error) {
      console.error('Error inviting user:', error);
      showToast('Error sending invitation', 'error');
    }
  };

  const handleEditCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;

    try {
      await CompanyService.updateCompany(selectedCompany.id, {
        name: editCompany.name,
        description: editCompany.description,
        industry: editCompany.industry,
        website: editCompany.website,
      });

      showToast('Company updated successfully!', 'success');
      setShowEditCompany(false);
      loadUserCompanies(); // Reload companies to show updated data
    } catch (error) {
      console.error('Error updating company:', error);
      showToast('Error updating company', 'error');
    }
  };

  const handleOpenEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setEditCompany({
      name: company.name,
      description: company.description || '',
      industry: company.industry || '',
      website: company.website || '',
    });
    // Load the specific company's details and members
    loadCompanyDetails(company.id);
    setShowEditCompany(true);
  };

  const handleUpdateMemberRole = async (memberId: string, newRole: UserRole) => {
    // Find the member to check permissions
    const member = members.find(m => m.id === memberId);
    if (!member) return;
    
    if (!canChangeMemberRole(member)) {
      showToast('You do not have permission to change this member\'s role', 'error');
      return;
    }
    
    try {
      const permissions = CompanyService.getDefaultPermissions(newRole);
      await CompanyService.updateMemberRole(memberId, newRole, permissions);
      showToast('Member role updated successfully', 'success');
      loadCompanyDetails(selectedCompany!.id);
    } catch (error) {
      console.error('Error updating member role:', error);
      showToast('Error updating member role', 'error');
    }
  };

  // Check if current user can remove members
  const canRemoveMember = (member: CompanyMember): boolean => {
    // MASTER can remove anyone
    if (currentUserRole === 'MASTER') return true;
    
    // Users cannot remove themselves
    if (member.userId === currentUser?.uid) return false;
    
    // Only ADMIN, MANAGER, and OWNER can remove members
    return ['ADMIN', 'MANAGER', 'OWNER'].includes(currentUserRole);
  };

  // Check if current user can change member roles
  const canChangeMemberRole = (member: CompanyMember): boolean => {
    // MASTER can change anyone's role
    if (currentUserRole === 'MASTER') return true;
    
    // Users cannot change their own role
    if (member.userId === currentUser?.uid) return false;
    
    // Only ADMIN, MANAGER, and OWNER can change roles
    return ['ADMIN', 'MANAGER', 'OWNER'].includes(currentUserRole);
  };

  const handleRemoveMember = async (member: CompanyMember) => {
    if (!selectedCompany) return;
    
    if (!canRemoveMember(member)) {
      showToast('You do not have permission to remove this member', 'error');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this member?')) return;

    try {
      await CompanyService.removeCompanyMember(selectedCompany.id, member.userId);
      showToast('Member removed successfully', 'success');
      loadCompanyDetails(selectedCompany.id);
    } catch (error) {
      console.error('Error removing member:', error);
      showToast('Error removing member', 'error');
    }
  };

  if (loading) {
    return (
      <div className="company-management">
        <div className="loading">Loading companies...</div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className="company-header">
        <h2>Company Management</h2>
        {currentUserRole === 'MASTER' && (
          <button 
            className="btn-primary"
            onClick={() => setShowCreateCompany(true)}
          >
            + Create Company
          </button>
        )}
      </div>

      {/* Company List */}
      <div className="company-list">
        {companies.map((company) => (
          <div 
            key={company.id}
            className={`company-card ${selectedCompany?.id === company.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedCompany(company);
              loadCompanyDetails(company.id);
            }}
            onDoubleClick={() => {
              onCompanySelect(company.id);
            }}
            title="Single click: View details | Double click: Switch to Dashboard"
          >
            <div className="company-info">
              <h3>{company.name}</h3>
              <p>{company.description}</p>
              <div className="company-meta">
                <span className="industry">{company.industry}</span>
                <span className="website">{company.website}</span>
              </div>
            </div>
            <div className="company-actions">
              <button 
                className="btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onCompanySelect(company.id);
                }}
                title="Switch to Dashboard"
              >
                📊 Dashboard
              </button>
              <button 
                className="btn-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenEditCompany(company);
                }}
                title="Manage Company"
              >
                Manage
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Company Details */}
      {selectedCompany && (
        <div className="company-details">
          <div className="details-header">
            <h3>{selectedCompany.name} - Details</h3>
            <div className="details-actions">
              <button 
                className="btn-primary"
                onClick={() => onCompanySelect(selectedCompany.id)}
                title="Switch to Dashboard"
              >
                📊 Dashboard
              </button>
              <button 
                className="btn-secondary"
                onClick={() => handleOpenEditCompany(selectedCompany)}
                title="Edit Company Details"
              >
                ✏️ Edit Company
              </button>
              {(currentUserRole === 'admin' || currentUserRole === 'master') && (
                <button 
                  className="btn-primary"
                  onClick={() => setShowInviteUser(true)}
                >
                  + Invite User
                </button>
              )}
            </div>
          </div>

          {/* Members */}
          <div className="members-section">
            <div className="section-header">
              <h4>Members ({members.length})</h4>
              <button 
                onClick={() => selectedCompany && loadCompanyDetails(selectedCompany.id)}
                className="refresh-members-btn"
                title="Refresh member list"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="members-list">
              {members.map((member) => (
                <div key={member.id} className="member-card">
                  <div className="member-info">
                    <div className="member-identity">
                      {member.nickname && (
                        <span className="member-nickname">{member.nickname}</span>
                      )}
                      <span className="member-email" title={member.email || member.userId}>
                        {member.displayName || member.email || member.userId}
                      </span>
                    </div>
                    <span className={`member-role role-${member.role}`}>{member.role}</span>
                    <span className={`member-status status-${member.status}`}>{member.status}</span>
                  </div>
                  <div className="member-actions">
                    {canChangeMemberRole(member) ? (
                      <select 
                        value={member.role}
                        onChange={(e) => handleUpdateMemberRole(member.id, e.target.value as UserRole)}
                        className="role-select"
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                    ) : (
                      <span className="role-display">{member.role}</span>
                    )}
                    {canRemoveMember(member) ? (
                      <button 
                        className="btn-danger"
                        onClick={() => handleRemoveMember(member)}
                      >
                        Remove
                      </button>
                    ) : (
                      <span className="no-action">No Actions</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pending Invitations */}
          {invitations.length > 0 && (
            <div className="invitations-section">
              <h4>Pending Invitations ({invitations.length})</h4>
              <div className="invitations-list">
                {invitations.map((invitation) => (
                  <div key={invitation.id} className="invitation-card">
                    <div className="invitation-info">
                      <span className="invitation-email">{invitation.email}</span>
                      <span className={`invitation-role role-${invitation.role}`}>{invitation.role}</span>
                      <span className="invitation-date">
                        Invited: {new Date(invitation.invitedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="invitation-status">
                      <span className={`status-${invitation.status}`}>{invitation.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateCompany && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Company</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCreateCompany(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateCompany} className="modal-form">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newCompany.description}
                  onChange={(e) => setNewCompany({ ...newCompany, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={newCompany.industry}
                  onChange={(e) => setNewCompany({ ...newCompany, industry: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={newCompany.website}
                  onChange={(e) => setNewCompany({ ...newCompany, website: e.target.value })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateCompany(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditCompany && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Company Details</h3>
              <button 
                className="close-btn"
                onClick={() => setShowEditCompany(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditCompany} className="modal-form">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={editCompany.name}
                  onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={editCompany.industry}
                  onChange={(e) => setEditCompany({ ...editCompany, industry: e.target.value })}
                  placeholder="e.g., Technology, Finance, Manufacturing"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={editCompany.website}
                  onChange={(e) => setEditCompany({ ...editCompany, website: e.target.value })}
                  placeholder="https://www.example.com"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editCompany.description}
                  onChange={(e) => setEditCompany({ ...editCompany, description: e.target.value })}
                  placeholder="Brief description of your company..."
                  rows={4}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditCompany(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Update Company
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Invite User to {selectedCompany?.name}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowInviteUser(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleInviteUser} className="modal-form">
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteUser.email}
                  onChange={(e) => setInviteUser({ ...inviteUser, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={inviteUser.role}
                  onChange={(e) => setInviteUser({ ...inviteUser, role: e.target.value as UserRole })}
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowInviteUser(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
